import React, { useMemo, useState } from "react";
import { CheckIcon, CloseIcon, EditIcon, PinIcon, PlusIcon, RefreshIcon, SearchIcon, TrashIcon } from "./Icons";
import { historyGroup } from "../utils";

const GROUP_ORDER = ["Pinned", "Today", "Yesterday", "Previous 7 days", "Previous 30 days", "Older"];

function Item({ conv, active, onSelect, onRename, onDelete, onPin }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(conv.title);
  const [confirming, setConfirming] = useState(false);

  const commit = () => {
    const t = title.trim();
    if (t && t !== conv.title) onRename(conv.id, t);
    else setTitle(conv.title);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="bdai-conv bdai-conv-editing">
        <input
          className="bdai-conv-input"
          value={title}
          autoFocus
          maxLength={255}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              e.stopPropagation();
              setTitle(conv.title);
              setEditing(false);
            }
          }}
          onBlur={commit}
          aria-label="Conversation title"
        />
      </div>
    );
  }

  if (confirming) {
    return (
      <div className="bdai-conv bdai-conv-confirm">
        <span>Delete this chat?</span>
        <button type="button" className="bdai-icon-btn bdai-icon-btn-sm bdai-danger" onClick={() => onDelete(conv.id)} title="Delete">
          <CheckIcon size={14} />
        </button>
        <button type="button" className="bdai-icon-btn bdai-icon-btn-sm" onClick={() => setConfirming(false)} title="Cancel">
          <CloseIcon size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className={`bdai-conv ${active ? "bdai-conv-active" : ""}`}>
      <button type="button" className="bdai-conv-main" onClick={() => onSelect(conv.id)} title={conv.title}>
        {conv.pinned && <PinIcon size={12} className="bdai-conv-pin" />}
        <span className="bdai-conv-title">{conv.title || "Untitled chat"}</span>
      </button>
      <div className="bdai-conv-actions">
        <button type="button" className="bdai-icon-btn bdai-icon-btn-xs" onClick={() => onPin(conv.id, !conv.pinned)} title={conv.pinned ? "Unpin" : "Pin"}>
          <PinIcon size={13} />
        </button>
        <button type="button" className="bdai-icon-btn bdai-icon-btn-xs" onClick={() => {
            setTitle(conv.title);
            setEditing(true);
          }}
          title="Rename"
        >
          <EditIcon size={13} />
        </button>
        <button type="button" className="bdai-icon-btn bdai-icon-btn-xs" onClick={() => setConfirming(true)} title="Delete">
          <TrashIcon size={13} />
        </button>
      </div>
    </div>
  );
}

export default function ConversationSidebar({
  conversations, loading, error, activeId, onSelect, onNew, onRename, onDelete, onPin, onRefresh, onClose, overlay,
}) {
  const [query, setQuery] = useState("");

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? conversations.filter((c) => (c.title || "").toLowerCase().includes(q)) : conversations;
    const out = {};
    list.forEach((c) => {
      const g = c.pinned ? "Pinned" : historyGroup(c.updated_at || c.created_at);
      (out[g] = out[g] || []).push(c);
    });
    return GROUP_ORDER.filter((g) => out[g]).map((g) => [g, out[g]]);
  }, [conversations, query]);

  return (
    <aside className={`bdai-sidebar ${overlay ? "bdai-sidebar-overlay" : ""}`} aria-label="Chat history">
      <div className="bdai-sidebar-top">
        <button type="button" className="bdai-btn bdai-btn-primary bdai-btn-block" onClick={onNew}>
          <PlusIcon size={15} /> New chat
        </button>
        {overlay && (
          <button type="button" className="bdai-icon-btn" onClick={onClose} title="Close history" aria-label="Close history">
            <CloseIcon size={16} />
          </button>
        )}
      </div>
      <div className="bdai-sidebar-search">
        <SearchIcon size={14} />
        <input type="search" placeholder="Search chats…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search chats" />
        <button type="button" className="bdai-icon-btn bdai-icon-btn-xs" onClick={onRefresh} title="Refresh" aria-label="Refresh history">
          <RefreshIcon size={13} className={loading ? "bdai-spin" : ""} />
        </button>
      </div>
      <div className="bdai-sidebar-list">
        {error && <div className="bdai-sidebar-empty bdai-text-danger">{error}</div>}
        {!error && !loading && !groups.length && (
          <div className="bdai-sidebar-empty">{query ? "No chats match your search." : "No chats yet. Ask your first question!"}</div>
        )}
        {loading && !conversations.length && (
          <div className="bdai-skeleton-list">
            {[0, 1, 2, 3].map((i) => <div key={i} className="bdai-skeleton" />)}
          </div>
        )}
        {groups.map(([label, items]) => (
          <div key={label} className="bdai-conv-group">
            <div className="bdai-conv-group-label">{label}</div>
            {items.map((c) => (
              <Item key={c.id} conv={c} active={c.id === activeId} onSelect={onSelect} onRename={onRename} onDelete={onDelete} onPin={onPin} />
            ))}
          </div>
        ))}
      </div>
    </aside>
  );
}
